import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore.js';

type MicState = 'idle' | 'recording' | 'busy';

interface Props {
  onText: (text: string) => void;
  size?: number; // tamanho do ícone
  title?: string;
}

// Botão de microfone: grava áudio (MediaRecorder) e envia pra
// POST /api/ai/transcribe — o texto transcrito volta via onText().
export function MicButton({ onText, size = 16, title = 'Ditar por voz' }: Props) {
  const workspaceId = useStore((s) => s.workspaceId);
  const [state, setState] = useState<MicState>('idle');
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => () => {
    recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mime.split(';')[0] });
        chunksRef.current = [];
        if (blob.size < 1000) { setState('idle'); return; } // gravação vazia/acidental
        setState('busy');
        try {
          const fd = new FormData();
          fd.append('file', blob, 'audio.webm');
          const res = await fetch(`/api/ai/transcribe?workspaceId=${workspaceId}`, {
            method: 'POST',
            body: fd,
            credentials: 'include',
          });
          if (res.status === 403) throw new Error('Consentimento de IA necessário (perfil → privacidade).');
          if (res.status === 402) throw new Error('Nenhum provider de transcrição configurado.');
          if (!res.ok) throw new Error('Falha na transcrição.');
          const data = (await res.json()) as { text: string };
          if (data.text) onText(data.text);
          else setError('Nada reconhecido.');
        } catch (err: any) {
          setError(err?.message ?? 'Falha na transcrição.');
        } finally {
          setState('idle');
        }
      };
      rec.start();
      recorderRef.current = rec;
      setState('recording');
    } catch {
      setError('Microfone indisponível (verifique permissões).');
      setState('idle');
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
    recorderRef.current = null;
  }

  const color = state === 'recording' ? '#ff4d6a' : state === 'busy' ? 'var(--muted)' : 'var(--neon)';

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => {
          if (state === 'recording') stopRecording();
          else if (state === 'idle') void startRecording();
        }}
        disabled={state === 'busy'}
        title={state === 'recording' ? 'Parar e transcrever' : title}
        aria-label={state === 'recording' ? 'Parar gravação' : title}
        style={{
          width: size + 12,
          height: size + 12,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: state === 'recording' ? 'rgba(255,77,106,0.14)' : 'transparent',
          border: 'none',
          borderRadius: 6,
          color,
          cursor: state === 'busy' ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          animation: state === 'recording' ? 'mic-pulse 1.2s ease-in-out infinite' : undefined,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: size }}>
          {state === 'busy' ? 'hourglass_top' : state === 'recording' ? 'stop_circle' : 'mic'}
        </span>
      </button>
      {error && (
        <span
          role="alert"
          onClick={() => setError(null)}
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 200,
            background: 'rgba(20,8,12,0.95)', border: '1px solid rgba(255,77,106,0.4)',
            color: '#ff8fa0', borderRadius: 6, padding: '4px 8px', fontSize: 11,
            whiteSpace: 'nowrap', cursor: 'pointer',
          }}
        >
          {error}
        </span>
      )}
      <style>{`@keyframes mic-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(255,77,106,0.35); } 50% { box-shadow: 0 0 0 6px rgba(255,77,106,0); } }`}</style>
    </span>
  );
}
